import classNames from "classnames";
import { useEffect, useState } from "react";
import { parseStringToNumber } from "../../../../../helperFunctions";
import { useGetMemberContributionsDetailsQuery } from "../../../../../redux/features/contributions/contributions.api";
import {
  SingleCooperativeDetails,
  SingleMemberContributionDetails,
} from "../../../../../redux/features/cooperative/cooperative.type";
import { useRequestLoanMutation } from "../../../../../redux/features/loans/loans.api";
import {
  LoanProduct,
  RequestLoanPayload,
} from "../../../../../redux/features/loans/loans.type";
import { BackButton } from "../../../../buttons/backButton";
import { PageLoader } from "../../../../loaders/pageLoader";
import { ProgressTrail } from "../../../../progress/progressTrail";
import { EmptyState } from "../../../../states/emptyState";
import { GuarantorInformation } from "./guarantor";
import { RequestLoanInformation } from "./loanInformation";
import { RepaymentDetails } from "./repaymentDetails";
import { ViewRepaymentDetails } from "./viewRepaymentDetails";

type Steps = 1 | 2 | 3 | 4 | 5;

const checkChooseGuarantor = (
  selectedLoanConfig?: LoanProduct,
  memberContributionDetails?: SingleMemberContributionDetails,
  finalPayload?: Partial<RequestLoanPayload>
) => {
  if (
    (selectedLoanConfig?.MaxGuarantor ?? 0) > 0 &&
    (parseStringToNumber(memberContributionDetails?.Balance) ?? 0) <
      (finalPayload?.loanAmount ?? 0)
  ) {
    return true;
  } else {
    return false;
  }
};

export const RequestLoanFlow = ({
  cooperativeDetails,
  onSuccess,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: () => void;
}) => {
  const [step, setStep] = useState<Steps>(1);
  const [selectedLoanConfig, setSelectedLoanConfig] = useState<LoanProduct>();
  const [chooseGuarantors, setChooseGuarantors] = useState<boolean>(false);
  const [finalPayload, setFinalPayload] =
    useState<Partial<RequestLoanPayload>>();

  const {
    data: memberContributionDetails,
    isFetching: fetchingMemberContributionsDetails,
  } = useGetMemberContributionsDetailsQuery(
    {
      coop: cooperativeDetails.id,
      logs: false,
      limit: 0,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [requestLoan, { isLoading: requestingLoan }] = useRequestLoanMutation();

  useEffect(() => {
    setSelectedLoanConfig(
      cooperativeDetails?.Settings?.loan?.find(
        (l) => l.id === finalPayload?.loanproductID
      )
    );
  }, [cooperativeDetails, finalPayload]);

  useEffect(() => {
    setChooseGuarantors(
      checkChooseGuarantor(
        selectedLoanConfig,
        memberContributionDetails?.data,
        finalPayload
      )
    );
  }, [selectedLoanConfig, memberContributionDetails, finalPayload]);

  return (
    <div className="flex-1 flex flex-col">
      <div
        className={classNames("mb-5", {
          hidden: [1, 5].includes(step),
        })}
      >
        <BackButton
          backIcon="back-caret"
          text="Previous"
          onClick={() => {
            if (step === 3 && !chooseGuarantors) {
              setStep(1);
            } else {
              setStep((step - 1) as Steps);
            }
          }}
        />
      </div>

      <div
        className={classNames("mb-6", {
          hidden: step >= 4,
        })}
      >
        <ProgressTrail
          steps={
            !chooseGuarantors
              ? ["Loan Information", "Loan Summary"]
              : ["Loan Information", "Guarantors", "Loan Summary"]
          }
          currentStep={step}
          diasbledIndexes={[]}
        />
      </div>

      {fetchingMemberContributionsDetails || requestingLoan ? (
        <PageLoader
          variant="threedots"
          className="my-auto !min-h-0 !py-0 absolute inset-0 bg-white z-10 opacity-80"
        />
      ) : undefined}

      <div className="flex-1">
        {step === 1 &&
        !fetchingMemberContributionsDetails &&
        memberContributionDetails?.data ? (
          <RequestLoanInformation
            cooperativeDetails={cooperativeDetails}
            finalPayload={finalPayload}
            setFinalPayload={setFinalPayload}
            onSuccess={(data) => {
              const chooseGuarantors_ = checkChooseGuarantor(
                selectedLoanConfig,
                memberContributionDetails?.data,
                data
              );

              if (chooseGuarantors_) {
                setStep(2);
              } else {
                setStep(3);
              }
            }}
            selectedLoanProduct={selectedLoanConfig}
            memberContributionsDetails={memberContributionDetails.data}
          />
        ) : undefined}

        {step === 2 &&
        !fetchingMemberContributionsDetails &&
        memberContributionDetails?.data ? (
          <GuarantorInformation
            cooperativeDetails={cooperativeDetails}
            finalPayload={finalPayload}
            setFinalPayload={setFinalPayload}
            onSuccess={() => {
              setStep(3);
            }}
            selectedLoanProduct={selectedLoanConfig}
            memberContributionsDetails={memberContributionDetails.data}
          />
        ) : undefined}

        {step === 3 &&
        !fetchingMemberContributionsDetails &&
        memberContributionDetails?.data ? (
          <ViewRepaymentDetails
            cooperativeDetails={cooperativeDetails}
            finalPayload={finalPayload}
            onSuccess={() => {
              setStep(4);
            }}
            selectedLoanProduct={selectedLoanConfig}
          />
        ) : undefined}

        {step === 4 &&
        !fetchingMemberContributionsDetails &&
        memberContributionDetails?.data ? (
          <RepaymentDetails
            cooperativeDetails={cooperativeDetails}
            finalPayload={finalPayload}
            setFinalPayload={setFinalPayload}
            onSuccess={async (data) => {
              const resp = await requestLoan({
                ...data,
                coop: cooperativeDetails.id,
                guarantor: data.guarantor ?? [],
              } as RequestLoanPayload);
              if (resp.data?.success) {
                setStep(5);
              }
            }}
            selectedLoanProduct={selectedLoanConfig}
          />
        ) : undefined}

        {step === 5 ? (
          <EmptyState
            image={<img src={"/assets/images/success.svg"} alt="success" />}
            titleClassName="max-w-[267px]"
            title={`Loan request submitted`}
            subtitle="Your loan request is now being processed for approval."
            actionText={"View loans"}
            actionButtonVariant="primary"
            onClick={() => {
              onSuccess();
            }}
            className="mt-[100px]"
          />
        ) : undefined}
      </div>
    </div>
  );
};
